<script setup lang="ts">
import PDataTable from 'primevue/datatable'
import PColumn from 'primevue/column'
import PInputText from 'primevue/inputtext'
import PSelect from 'primevue/select'
import PInputGroup from 'primevue/inputgroup'
import PInputGroupAddon from 'primevue/inputgroupaddon'
import { ref } from 'vue'
import type { DashboardScoreDiagnostic } from '@/core/domain/dashboard/DashboardScoreDiagnostic.ts'
import { QuizType } from '@/core/domain/resources/enums/QuizType.ts'
import CDashboardClassroomResultsDiagnosticTableTaskResult from '@/presentation/components/dashboard/CDashboardClassroomResultsDiagnosticTableTaskResult.vue'

// Props
const props = defineProps<{
  dataSource: DashboardScoreDiagnostic[]
  teachersOptions: { label: string; value: string }[]
  isLoading: boolean
  type: QuizType.Values
}>()

// Data
const filters = ref({
  global: { value: null, matchMode: 'contains' },
  teacherFullname: { value: null, matchMode: 'contains' }
})
</script>

<template>
  <p-data-table
    :value="dataSource"
    dataKey="id"
    class="overflow-x-auto text-sm"
    size="small"
    :loading="isLoading"
    v-model:filters="filters"
    removableSort
    scrollable
    sortMode="multiple"
    paginator
    :rows="10"
    :rowsPerPageOptions="[5, 10, 20, 50]"
    :globalFilterFields="['firstname', 'lastname', 'teacherFullname']"
    stripedRows
    responsiveLayout="stack"
  >
    <!-- Header avec filtres -->
    <template #header>
      <div class="flex flex-col items-center justify-between gap-2 sm:flex-row">
        <p-input-group class="w-auto">
          <p-input-group-addon>
            <i class="pi pi-search" />
          </p-input-group-addon>
          <p-input-text
            v-model="filters['global'].value"
            placeholder="Rechercher un élève"
            class="w-full sm:w-auto"
          />
        </p-input-group>

        <p-select
          v-model="filters['teacherFullname'].value"
          :options="teachersOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Filtrer par enseignant"
          class="w-full sm:w-auto"
        />
      </div>
    </template>

    <template #empty>Aucune donnée trouvée.</template>
    <template #loading>Chargement des données...</template>

    <!-- Colonnes du tableau -->
    <p-column field="firstname" frozen :sortable="true" header="Prénom" class="w-48" data-maskable>
      <template #body="{ data }: { data: DashboardScoreDiagnostic }">
        <p class="m-0" data-maskable>{{ data.firstname }}</p>
      </template>
    </p-column>

    <p-column field="lastname" frozen :sortable="true" header="Nom" class="w-48" data-maskable>
      <template #body="{ data }: { data: DashboardScoreDiagnostic }">
        <p class="m-0" data-maskable>{{ data.lastname }}</p>
      </template>
    </p-column>

    <p-column
      field="teacherFullname"
      :sortable="true"
      header="Enseignant"
      class="w-48"
      data-maskable
    >
      <template #body="{ data }: { data: DashboardScoreDiagnostic }">
        <p class="m-0" data-maskable>
          <template v-if="data.teacherFirstname">
            {{ data.teacherFirstname }} {{ data.teacherLastname }}
          </template>
          <template v-else> - </template>
        </p>
      </template>
    </p-column>

    <p-column
      v-for="(task, i) in dataSource[0].tasks"
      field="diagScorePercentRounded"
      :header="task.learningModuleTitle"
      class="w-48"
    >
      <template #body="{ data }: { data: DashboardScoreDiagnostic }">
        <CDashboardClassroomResultsDiagnosticTableTaskResult
          :task-result="data.tasks[i]"
          :type="type"
        />
      </template>
    </p-column>
  </p-data-table>
</template>
